import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { toast } from 'sonner';
import { Activity, Loader2, PlayCircle, AlertTriangle, CheckCircle2, Sparkles, ArrowUpDown, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ScoreCircle({ score }) {
  const color = score >= 70 ? 'text-emerald-400 border-emerald-500' : score >= 50 ? 'text-amber-400 border-amber-500' : 'text-red-400 border-red-500';
  return (
    <div className={`w-14 h-14 rounded-full border-2 flex items-center justify-center font-bold ${color}`} data-testid="score-circle">{score}</div>
  );
}

export default function AdminContentHealth() {
  const { authFetch } = useAdmin();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [sortBy, setSortBy] = useState('score_asc'); // score_asc | score_desc | title_asc | title_desc

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/admin/content-health/latest`);
      if (r.ok) setReport(await r.json());
    } finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  async function analyze() {
    setRunning(true);
    try {
      const r = await authFetch(`${API}/admin/content-health/analyze`, { method: 'POST' });
      setReport(await r.json());
      toast.success('Analiza završena');
    } catch { toast.error('Greška'); }
    finally { setRunning(false); }
  }

  async function runQuickFix(ids, labelAction) {
    if (!ids || ids.length === 0) { toast.info('Nema članaka za popraviti'); return; }
    setFixing(true);
    const tid = toast.loading(`${labelAction} (${ids.length} članaka)…`);
    try {
      const r = await authFetch(`${API}/admin/content-health/quick-fix`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ article_ids: ids }),
      });
      const d = await r.json();
      toast.success(`Ažurirano ${d.updated}/${d.requested}`, { id: tid });
      setSelected(new Set());
      await analyze();
    } catch {
      toast.error('Greška', { id: tid });
    } finally {
      setFixing(false);
    }
  }

  async function quickFixSelected() {
    await runQuickFix([...selected], 'Popravljam odabrane');
  }

  async function quickFixAllBelow60() {
    const ids = (report?.articles || []).filter(a => a.score < 60).map(a => a.article_id);
    if (!window.confirm(`Automatski generiraj SEO naslov i meta opis za ${ids.length} članaka sa skorom <60?`)) return;
    await runQuickFix(ids, 'Auto-fix <60');
  }

  async function suggest(articleId) {
    const tid = toast.loading('Generiram preporuke…');
    try {
      const r = await authFetch(`${API}/admin/content-health/ai-suggestions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ article_id: articleId }),
      });
      const d = await r.json();
      toast.success('Gotovo', { id: tid });
      window.alert(JSON.stringify(d, null, 2));
    } catch { toast.error('Greška', { id: tid }); }
  }

  const sortedArticles = useMemo(() => {
    const arr = [...(report?.articles || [])];
    const cmp = {
      score_asc:  (a, b) => a.score - b.score,
      score_desc: (a, b) => b.score - a.score,
      title_asc:  (a, b) => (a.title || '').localeCompare(b.title || '', 'hr'),
      title_desc: (a, b) => (b.title || '').localeCompare(a.title || '', 'hr'),
    }[sortBy] || ((a, b) => a.score - b.score);
    arr.sort(cmp);
    return arr.slice(0, 100);
  }, [report, sortBy]);

  const below60Count = useMemo(() => (report?.articles || []).filter(a => a.score < 60).length, [report]);

  if (loading) return <div className="p-10 text-center text-zinc-500"><Loader2 className="inline animate-spin mr-2" /> Učitavanje…</div>;

  return (
    <div className="p-6 max-w-7xl" data-testid="admin-content-health-page">
      <header className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-3xl text-zinc-100 flex items-center gap-3"><Activity size={28} className="text-red-500" /> Zdravlje sadržaja</h1>
          <p className="text-xs text-zinc-500 mt-1">Bodovi po članku 0-100. Zeleno ≥70, žuto ≥50, crveno &lt;50.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {below60Count > 0 && (
            <button
              onClick={quickFixAllBelow60}
              disabled={fixing}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded-md flex items-center gap-2 disabled:opacity-50"
              data-testid="quickfix-below-60-btn"
              title="Automatski popravi SEO naslov i meta opis za članke sa skorom < 60"
            >
              {fixing ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Quick Fix &lt;60 ({below60Count})
            </button>
          )}
          {selected.size > 0 && (
            <button
              onClick={quickFixSelected}
              disabled={fixing}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-md flex items-center gap-2 disabled:opacity-50"
              data-testid="quickfix-selected-btn"
            >
              {fixing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Quick Fix odabranih ({selected.size})
            </button>
          )}
          <button onClick={analyze} disabled={running} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md flex items-center gap-2 disabled:opacity-50" data-testid="analyze-btn">
            {running ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />} Pokreni analizu
          </button>
        </div>
      </header>

      {!report ? (
        <div className="text-center py-16 text-zinc-500 border border-zinc-800 rounded-xl bg-zinc-900/40">
          Još nema izvještaja. Klikni "Pokreni analizu".
        </div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-3 mb-6">
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4"><div className="text-xs text-zinc-500">Analizirano</div><div className="text-2xl font-bold">{report.total_analyzed}</div></div>
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4"><div className="text-xs text-zinc-500">Prosječni bod</div><div className="text-2xl font-bold">{report.average_score}</div></div>
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4"><div className="text-xs text-zinc-500">Članci &lt; 60</div><div className="text-2xl font-bold text-amber-400">{below60Count}</div></div>
            {Object.entries(report.issue_summary || {}).slice(0, 1).map(([k, v]) => (
              <div key={k} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
                <div className="text-xs text-zinc-500 truncate">{k}</div>
                <div className="text-2xl font-bold text-amber-400">{v}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-zinc-500">Prikazano: {sortedArticles.length} članaka</div>
            <label className="text-xs text-zinc-400 flex items-center gap-2">
              <ArrowUpDown size={12} />
              Sortiranje:
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-200 focus:border-red-500 outline-none"
                data-testid="health-sort-select"
              >
                <option value="score_asc">Najniži bod → najviši</option>
                <option value="score_desc">Najviši bod → najniži</option>
                <option value="title_asc">Naslov A → Z</option>
                <option value="title_desc">Naslov Z → A</option>
              </select>
            </label>
          </div>

          <div className="space-y-2" data-testid="health-articles-list">
            {sortedArticles.map(a => (
              <div key={a.article_id} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 flex items-center gap-4" data-testid={`health-row-${a.article_id}`}>
                <input type="checkbox" checked={selected.has(a.article_id)}
                       onChange={e => setSelected(s => { const n = new Set(s); e.target.checked ? n.add(a.article_id) : n.delete(a.article_id); return n; })}
                       data-testid={`health-check-${a.article_id}`} />
                <ScoreCircle score={a.score} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-zinc-100 truncate">{a.title}</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {a.issues.slice(0, 4).map(i => (
                      <span key={i.code} className={`text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5 ${
                        i.severity === 'high' ? 'bg-red-500/10 text-red-400' : i.severity === 'medium' ? 'bg-amber-500/10 text-amber-400' : 'bg-zinc-800 text-zinc-400'
                      }`}>
                        {i.severity === 'high' ? <AlertTriangle size={9} /> : <CheckCircle2 size={9} />}{i.label}
                      </span>
                    ))}
                  </div>
                </div>
                <button onClick={() => suggest(a.article_id)} className="px-3 py-1 text-xs bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 rounded flex items-center gap-1" data-testid={`suggest-btn-${a.article_id}`}>
                  <Sparkles size={11} /> AI preporuke
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
